/**
 * EmailDeliverabilityBackgroundIndicator
 *
 * Panel flotante en la esquina inferior derecha de la pantalla. Muestra
 * las identidades de Email Deliverability que el usuario envio a "segundo
 * plano" desde el wizard mientras se estaban verificando.
 *
 * Patron espejo del `BackgroundUploadNotification` de orbit:
 * - Header colapsable con icono animado mientras hay verificaciones activas.
 * - Lista expandible de items (cada identity).
 * - Boton X por item para descartar; boton "Limpiar finalizados" para
 *   eliminar las que ya estan en estado terminal (verified/failed).
 * - Click en un item navega a /integrations?manage=<id> (la pagina abre el
 *   panel de gestion del identity).
 *
 * Se monta una sola vez en el AppRouter dentro de RouteGuard (cross-app).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  CheckCircle,
  XCircle,
  Loader2,
  X,
  ChevronUp,
  ChevronDown,
  Trash2,
} from 'lucide-react';

import { useEmailDeliverabilityBackground } from '../hooks/useEmailDeliverabilityBackground';
import { useSendingIdentity } from '../hooks/useSendingIdentities';
import {
  updateBackgroundIdentityStatus as storageUpdateStatus,
  type BackgroundIdentity,
} from '../utils/emailDeliverabilityBackground';
import type { SendingIdentityStatus } from '@/services/api/sendingIdentitiesService';

function formatRelativeShort(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function StatusIcon({ status }: { status: SendingIdentityStatus }) {
  if (status === 'verified') {
    return <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />;
  }
  if (status === 'failed') {
    return <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />;
  }
  // pending / verifying
  return <Loader2 className="h-5 w-5 text-blue-500 animate-spin flex-shrink-0" />;
}

function getStatusText(status: SendingIdentityStatus): string {
  switch (status) {
    case 'pending':
      return 'En cola para verificar...';
    case 'verifying':
      return 'Verificando contra AWS SES...';
    case 'verified':
      return 'Dominio verificado';
    case 'failed':
      return 'Verificacion fallida';
    case 'disabled':
      return 'Deshabilitada';
    default:
      return status;
  }
}

interface ItemRowProps {
  item: BackgroundIdentity;
  onRemove: () => void;
  onOpen: () => void;
}

/**
 * Sync wrapper: hace fetch del detail real de la identity y, si el status
 * de la API difiere del que tenemos cacheado en localStorage, lo sincroniza.
 *
 * Cubre el caso "el user volvio despues de 20 min y SES ya verifico mientras
 * el browser estaba cerrado" — sin esto el panel se quedaria pegado en
 * 'verifying' indefinidamente porque el WS solo emite eventos vivos, no
 * historicos. React Query refetcha al focus + cuando llega WS invalidate,
 * asi que es un fallback robusto sin polling explicito.
 */
function ItemRow({ item, onRemove, onOpen }: ItemRowProps) {
  // Solo fetch si el estado cacheado es no-terminal (ahorra calls inutiles
  // para items que ya quedaron en verified/failed/disabled).
  const isCachedTerminal =
    item.status === 'verified' ||
    item.status === 'failed' ||
    item.status === 'disabled';
  const { data: fresh } = useSendingIdentity(isCachedTerminal ? undefined : item.id);

  // Si el fetch trajo un status nuevo, actualizar storage. El custom event
  // que dispara `storageUpdateStatus` notifica al hook padre — el state se
  // refresca y este wrapper recibe el item con el status actualizado.
  useEffect(() => {
    if (!fresh) return;
    if (fresh.verification_status !== item.status) {
      storageUpdateStatus(item.id, fresh.verification_status);
    }
  }, [fresh, item.id, item.status]);

  const isActive =
    item.status === 'pending' || item.status === 'verifying';

  return (
    <div className="p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
      <div className="flex items-start gap-3">
        <div className="mt-0.5">
          <StatusIcon status={item.status} />
        </div>

        <button
          type="button"
          onClick={onOpen}
          className="flex-1 min-w-0 text-left"
        >
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {item.from_email}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {item.domain}
            {item.is_managed_by_owly && (
              <span className="ml-1 inline-block text-[10px] font-medium px-1.5 py-0 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                OWLY
              </span>
            )}
          </p>

          {isActive && (
            <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full w-1/3 rounded-full bg-blue-500 animate-pulse" />
            </div>
          )}

          <p
            className={`text-xs mt-1 ${
              item.status === 'failed'
                ? 'text-red-500'
                : item.status === 'verified'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {getStatusText(item.status)}
            <span className="ml-2 text-gray-400">
              ({formatRelativeShort(item.terminal_at ?? item.added_at)})
            </span>
          </p>
        </button>

        <button
          type="button"
          onClick={onRemove}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
          title="Descartar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export const EmailDeliverabilityBackgroundIndicator: React.FC = () => {
  const navigate = useNavigate();
  const { items, hasActive, activeCount, terminalCount, remove, clearTerminal } =
    useEmailDeliverabilityBackground();
  const [expanded, setExpanded] = useState(true);

  if (items.length === 0) return null;

  const handleOpen = (id: string) => {
    navigate(`/integrations?manage=${id}`);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header colapsable */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative flex-shrink-0">
              <Mail
                className={`h-5 w-5 ${
                  hasActive
                    ? 'text-blue-500 animate-pulse'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              />
              {hasActive && (
                <span className="absolute -top-1 -right-1 h-2 w-2 bg-blue-500 rounded-full animate-ping" />
              )}
            </div>
            <div className="text-left min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {hasActive
                  ? `Verificando ${activeCount} ${
                      activeCount === 1 ? 'dominio' : 'dominios'
                    }...`
                  : 'Verificaciones finalizadas'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {terminalCount > 0 && `${terminalCount} finalizadas`}
                {terminalCount === 0 && hasActive && 'Email Deliverability'}
              </p>
            </div>
          </div>
          {expanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
          )}
        </button>

        {/* Lista expandida */}
        {expanded && (
          <div className="max-h-80 overflow-y-auto">
            {items.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                onRemove={() => remove(item.id)}
                onOpen={() => handleOpen(item.id)}
              />
            ))}

            {terminalCount > 0 && (
              <div className="p-2 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={clearTerminal}
                  className="w-full px-3 py-2 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Limpiar finalizadas
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailDeliverabilityBackgroundIndicator;
